import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import {
    ArrowLeft,
    MessageCircle,
    Plus,
    SquarePen,
    Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { useSWRConfig } from 'swr';
import { z } from 'zod';

import { ActivityTimeline } from '@apps/web/components/admin/activity_timeline';
import { AddContactModal } from '@apps/web/components/admin/add_contact_modal';
import { CaregiverFormModal } from '@apps/web/components/admin/caregiver_form_modal';
import { ContactCard } from '@apps/web/components/admin/contact_card';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { Card } from '@apps/web/components/ui/card';
import { Spinner } from '@apps/web/components/ui/spinner';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { getApiErrorDetail } from '@apps/web/lib/known_api_errors';
import { useAdminCaregiver } from '@apps/web/swr_data_fetching/admin_org_record_queries';
import { type ContactType } from '@libraries/typescript/api_client';

const caregiverDetailSearchSchema = z.object({
    organizationId: z.string().optional(),
    search: z.string().optional(),
});

export const Route = createFileRoute('/_app/admin/caregivers/$caregiver-id')({
    head: () => ({
        meta: [
            {
                title: 'Caregiver Details | Phoebe Admin',
            },
        ],
    }),
    validateSearch: caregiverDetailSearchSchema,
    component: CaregiverDetailPage,
});

function CaregiverDetailPage() {
    const { 'caregiver-id': caregiverId } = Route.useParams();
    const returnSearch = Route.useSearch();

    return (
        <div className="h-full overflow-x-hidden overflow-y-auto">
            <div className="max-w-5xl p-6">
                <div className="mb-6">
                    <Link
                        to="/admin/caregivers"
                        search={returnSearch}
                        className="text-muted-foreground hover:text-foreground inline-flex items-center gap-2 text-sm"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back to Caregivers
                    </Link>
                </div>
                <CaregiverDetails caregiverId={caregiverId} />
            </div>
        </div>
    );
}

function CaregiverDetails({ caregiverId }: { caregiverId: string }) {
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isAddContactModalOpen, setIsAddContactModalOpen] = useState(false);
    const [isAddingContact, setIsAddingContact] = useState(false);
    const [removingContactId, setRemovingContactId] = useState<string | null>(
        null,
    );
    const [togglingPrimaryContactId, setTogglingPrimaryContactId] = useState<
        string | null
    >(null);
    const router = useRouter();
    const { mutate } = useSWRConfig();

    const {
        data: caregiver,
        isLoading,
        mutate: mutateCaregiver,
    } = useAdminCaregiver(caregiverId);

    const handleDelete = async () => {
        if (!caregiverId || !caregiver) {
            return;
        }

        const confirmed = window.confirm(
            `Are you sure you want to delete ${caregiver.name}? This action cannot be undone.`,
        );

        if (!confirmed) {
            return;
        }

        setIsDeleting(true);

        try {
            await getApiClient().delete({
                url: '/admin/caregivers/{caregiver_id}',
                pathParams: { caregiver_id: caregiverId },
            });

            toast.success('Caregiver deleted successfully', {
                persistNotification: false,
            });
            mutate(
                (key) =>
                    typeof key === 'string' &&
                    key.includes('/admin/caregivers'),
            );
            router.navigate({ to: '/admin/caregivers' });
        } catch (error) {
            toast.error(
                getApiErrorDetail(error) ?? 'Failed to delete caregiver',
                {
                    persistNotification: false,
                },
            );
            setIsDeleting(false);
        }
    };

    const handleAddContact = async (contactData: {
        type: ContactType;
        address: string;
        is_primary: boolean;
    }) => {
        if (!caregiverId || !caregiver) {
            return;
        }

        setIsAddingContact(true);

        try {
            await getApiClient().post({
                url: '/admin/caregivers/{caregiver_id}/contacts',
                pathParams: { caregiver_id: caregiverId },
                body: contactData,
            });

            toast.success('Contact added successfully', {
                persistNotification: false,
            });
            await mutateCaregiver();
        } catch (error) {
            toast.error(
                error instanceof Error
                    ? error.message
                    : 'Failed to add contact',
                { persistNotification: false },
            );
            throw error;
        } finally {
            setIsAddingContact(false);
        }
    };

    const handleRemoveContact = async (contactId: string) => {
        if (!caregiverId || !caregiver) {
            return;
        }

        const confirmed = window.confirm(
            'Are you sure you want to remove this contact? This action cannot be undone.',
        );
        if (!confirmed) {
            return;
        }

        setRemovingContactId(contactId);

        try {
            await getApiClient().delete({
                url: '/admin/caregivers/{caregiver_id}/contacts/{contact_id}',
                pathParams: {
                    caregiver_id: caregiverId,
                    contact_id: contactId,
                },
            });

            toast.success('Contact removed successfully', {
                persistNotification: false,
            });
            await mutateCaregiver();
        } catch (error) {
            toast.error('Failed to remove contact', {
                persistNotification: false,
            });
        } finally {
            setRemovingContactId(null);
        }
    };

    const handleTogglePrimary = async (contactId: string) => {
        if (!caregiverId || !caregiver) {
            return;
        }

        const contact = caregiver.contacts?.find((c) => c.id === contactId);
        if (!contact) {
            return;
        }

        if (contact.is_primary) {
            toast.error('This contact is already the primary contact.', {
                persistNotification: false,
            });
            return;
        }

        setTogglingPrimaryContactId(contactId);

        try {
            await getApiClient().delete({
                url: '/admin/caregivers/{caregiver_id}/contacts/{contact_id}',
                pathParams: {
                    caregiver_id: caregiverId,
                    contact_id: contactId,
                },
            });

            await getApiClient().post({
                url: '/admin/caregivers/{caregiver_id}/contacts',
                pathParams: { caregiver_id: caregiverId },
                body: {
                    type: contact.type,
                    address: contact.address,
                    is_primary: true,
                },
            });

            toast.success('Primary contact updated successfully', {
                persistNotification: false,
            });
            await mutateCaregiver();
        } catch (error) {
            toast.error('Failed to update primary contact', {
                persistNotification: false,
            });
        } finally {
            setTogglingPrimaryContactId(null);
        }
    };

    if (isLoading) {
        return (
            <div className="flex h-full items-center justify-center">
                <div className="text-muted-foreground flex items-center gap-2">
                    <Spinner />
                    <span>Loading caregiver details…</span>
                </div>
            </div>
        );
    }

    if (!caregiver) {
        return (
            <div className="text-muted-foreground flex h-full items-center justify-center">
                Caregiver not found
            </div>
        );
    }

    return (
        <div className="max-w-full min-w-0 space-y-6 overflow-hidden">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-semibold">Caregiver Details</h1>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsEditModalOpen(true)}
                        className="gap-2"
                    >
                        <SquarePen className="h-4 w-4" />
                        Edit
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDelete}
                        disabled={isDeleting}
                        className="gap-2 text-red-600 hover:text-red-700"
                    >
                        <Trash2 className="h-4 w-4" />
                        {isDeleting ? 'Deleting...' : 'Delete'}
                    </Button>
                    <Badge
                        variant={caregiver.is_active ? 'default' : 'secondary'}
                    >
                        {caregiver.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                </div>
            </div>
            <Card className="p-6">
                <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-4">
                        <div>
                            <h3 className="text-muted-foreground text-sm font-medium">
                                Name
                            </h3>
                            <p className="mt-1">{caregiver.name}</p>
                        </div>
                        <div>
                            <h3 className="text-muted-foreground text-sm font-medium">
                                Organization
                            </h3>
                            <p className="mt-1">
                                {caregiver.organization_name}
                            </p>
                        </div>
                        <div>
                            <h3 className="text-muted-foreground text-sm font-medium">
                                Roles
                            </h3>
                            <p className="mt-1 capitalize">
                                {caregiver.roles
                                    ?.map((role) => role.toUpperCase())
                                    .join(', ') || 'None'}
                            </p>
                        </div>
                        <div>
                            <h3 className="text-muted-foreground text-sm font-medium">
                                Status
                            </h3>
                            <p className="mt-1">
                                {caregiver.is_active ? 'Active' : 'Inactive'}
                            </p>
                        </div>
                    </div>
                    <div className="space-y-4">
                        <div>
                            <h3 className="text-muted-foreground text-sm font-medium">
                                Address
                            </h3>
                            <p className="mt-1">
                                {[
                                    caregiver.address_line_1,
                                    caregiver.address_line_2,
                                    [caregiver.city, caregiver.state]
                                        .filter(Boolean)
                                        .join(', '),
                                    caregiver.postal_code,
                                ]
                                    .filter(Boolean)
                                    .join(', ') || 'No address provided'}
                            </p>
                        </div>
                        <div>
                            <h3 className="text-muted-foreground text-sm font-medium">
                                Country
                            </h3>
                            <p className="mt-1">
                                {caregiver.country || 'Not specified'}
                            </p>
                        </div>
                        <div>
                            <h3 className="text-muted-foreground text-sm font-medium">
                                External ID
                            </h3>
                            <p className="mt-1 font-mono text-sm">
                                {caregiver.external_id || 'None'}
                            </p>
                        </div>
                        <div>
                            <h3 className="text-muted-foreground text-sm font-medium">
                                Source
                            </h3>
                            <p className="mt-1 capitalize">
                                {caregiver.source_system || 'System'}
                            </p>
                        </div>
                        <div>
                            <h3 className="text-muted-foreground text-sm font-medium">
                                Created
                            </h3>
                            <p className="mt-1">
                                {new Date(
                                    caregiver.created_at,
                                ).toLocaleString()}
                            </p>
                        </div>
                    </div>
                </div>
                {caregiver.licenses && caregiver.licenses.length > 0 && (
                    <div className="mt-6">
                        <h3 className="text-muted-foreground text-sm font-medium">
                            Licenses
                        </h3>
                        <div className="mt-2 flex flex-wrap gap-2">
                            {caregiver.licenses.map((license, index) => (
                                <Badge key={index} variant="outline">
                                    {typeof license === 'object'
                                        ? `${license['type']}: ${license['number']}`
                                        : license}
                                </Badge>
                            ))}
                        </div>
                    </div>
                )}
                {caregiver.skills && caregiver.skills.length > 0 && (
                    <div className="mt-6">
                        <h3 className="text-muted-foreground text-sm font-medium">
                            Skills
                        </h3>
                        <div className="mt-2 flex flex-wrap gap-2">
                            {caregiver.skills.map((skill) => (
                                <Badge key={skill} variant="outline">
                                    {skill.replace('_', ' ')}
                                </Badge>
                            ))}
                        </div>
                    </div>
                )}
                {caregiver.preferences_tags &&
                    caregiver.preferences_tags.length > 0 && (
                        <div className="mt-6">
                            <h3 className="text-muted-foreground text-sm font-medium">
                                Preferences
                            </h3>
                            <div className="mt-2 flex flex-wrap gap-2">
                                {caregiver.preferences_tags.map((tag) => (
                                    <Badge key={tag} variant="outline">
                                        {tag.replace('_', ' ')}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    )}
                {caregiver.notes && (
                    <div className="mt-6">
                        <h3 className="text-muted-foreground text-sm font-medium">
                            Notes
                        </h3>
                        <p className="mt-1">{caregiver.notes}</p>
                    </div>
                )}
            </Card>
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold">
                        Contacts ({caregiver.contact_count || 0})
                    </h2>
                    <Button
                        size="sm"
                        className="gap-2"
                        onClick={() => setIsAddContactModalOpen(true)}
                    >
                        <Plus className="h-4 w-4" />
                        Add Contact
                    </Button>
                </div>
                {caregiver.contacts && caregiver.contacts.length > 0 ? (
                    <div className="space-y-3">
                        {caregiver.contacts.map((contact) => (
                            <ContactCard
                                key={contact.id}
                                contact={contact}
                                onRemove={handleRemoveContact}
                                onTogglePrimary={handleTogglePrimary}
                                isRemoving={removingContactId === contact.id}
                                isTogglingPrimary={
                                    togglingPrimaryContactId === contact.id
                                }
                            />
                        ))}
                    </div>
                ) : (
                    <Card className="p-6">
                        <div className="text-muted-foreground text-center">
                            <MessageCircle className="mx-auto h-12 w-12 opacity-50" />
                            <h3 className="mt-4 text-lg font-semibold">
                                No contacts yet
                            </h3>
                            <p className="mt-2">
                                Add contact information to reach this caregiver
                            </p>
                        </div>
                    </Card>
                )}
            </div>
            <ActivityTimeline
                entityType="org_caregiver_record"
                entityId={caregiver.id}
                organizationId={caregiver.organization_id}
            />
            <AddContactModal
                isOpen={isAddContactModalOpen}
                onClose={() => setIsAddContactModalOpen(false)}
                onSubmit={handleAddContact}
                isSubmitting={isAddingContact}
            />
            <CaregiverFormModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                mode="edit"
                caregiverId={caregiverId}
                onSaveSuccess={() => mutateCaregiver()}
                initialData={{
                    mode: caregiver.mode,
                    organization_id: caregiver.organization_id,
                    user_id: caregiver.user_id || undefined,
                    name: caregiver.name,
                    address_line_1: caregiver.address_line_1 || '',
                    address_line_2: caregiver.address_line_2 || '',
                    city: caregiver.city || '',
                    state: caregiver.state || '',
                    postal_code: caregiver.postal_code || '',
                    country: caregiver.country || 'US',
                    roles: caregiver.roles || [],
                    licenses: caregiver.licenses || [],
                    skills: caregiver.skills || [],
                    preferences_tags: caregiver.preferences_tags || [],
                    is_active: caregiver.is_active,
                    notes: caregiver.notes || '',
                }}
            />
        </div>
    );
}
